package fr.frux.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.navigation3.runtime.NavBackStack
import androidx.navigation3.runtime.rememberNavBackStack
import kotlinx.serialization.json.Json

/**
 * Manages navigation state for Frux.
 *
 * This class provides high-level, type-safe navigation operations on top of the raw NavBackStack.
 * It handles authentication checks, back navigation, and other common navigation patterns.
 *
 * Key benefits over raw NavBackStack manipulation:
 * - Enforces authentication requirements
 * - Provides semantic operations (navigate, navigateBack, replaceWith, resetTo)
 * - Centralizes navigation logic for consistency
 *
 * Usage:
 * ```kotlin
 * val navigationState = rememberNavigationState()
 *
 * // Navigate to a route
 * navigationState.navigate(Route.TreeDetail(42))
 *
 * // Navigate back
 * navigationState.navigateBack()
 *
 * // Replace current route (e.g., after login)
 * navigationState.replaceWith(Route.Map)
 * ```
 */
@Stable
class NavigationState(
    val backStack: NavBackStack<Route>,
    private val isAuthenticated: () -> Boolean,
    /**
     * Porteur de [pendingRoute], sauvegardé par [rememberNavigationState] : une rotation pendant
     * le mur de connexion ne doit perdre ni la destination ni son contexte (centre de carte de
     * [Route.AddTree]). Le rechargement de page web n'est pas couvert.
     */
    private val pendingRouteState: MutableState<Route?> = mutableStateOf(null),
) {
    /**
     * Destination requested while unauthenticated, resumed by [onAuthenticated].
     */
    var pendingRoute: Route?
        get() = pendingRouteState.value
        private set(value) {
            pendingRouteState.value = value
        }

    /** Session ouverte à l'instant de la lecture, pour les écrans publics qui s'adaptent (#468). */
    val isSignedIn: Boolean
        get() = isAuthenticated()

    /**
     * Navigate to a route with optional authentication checks.
     *
     * If the route requires authentication and the user is not authenticated,
     * remembers it as pending and redirects to the Auth screen — exactly once,
     * even if several protected navigations happen in a row.
     *
     * @param route The destination route
     */
    fun navigate(route: Route) {
        if (route.requiresAuth && !isAuthenticated()) {
            pendingRoute = route
            if (backStack.lastOrNull() != Route.Auth) {
                backStack.add(Route.Auth)
            }
        } else {
            backStack.add(route)
        }
    }

    /**
     * Demande explicite de connexion depuis une fiche ouverte à l'anonyme (#264) : [returnTo] est
     * déjà affichée, l'action tentée dessus exige un compte. Même mécanique que [navigate] sur une
     * route murée (pendingRoute + Auth déduplié), mais sans passer par `requiresAuth` puisque
     * [returnTo] (TreeDetail/HarvestDetail) ne l'exige plus elle-même.
     */
    fun requestLogin(returnTo: Route) {
        pendingRoute = returnTo
        if (backStack.lastOrNull() != Route.Auth) {
            backStack.add(Route.Auth)
        }
    }

    /**
     * Called after a successful login/register.
     *
     * Resets the stack on Map (Auth must never be reachable via back) and
     * resumes the destination that triggered the redirect, if any.
     */
    fun onAuthenticated() {
        val target = pendingRoute
        pendingRoute = null
        backStack.clear()
        backStack.add(Route.Map)
        if (target != null && target != Route.Map) {
            backStack.add(target)
        }
    }

    /**
     * Navigate back by removing the last entry from the back stack.
     *
     * The stack is never emptied: NavDisplay refuses an empty stack (#692). A lone entry other
     * than the map (deep link) falls back on the map, the app's root (ADR 0009). A lone map
     * leaves the back gesture to the platform.
     *
     * @return true if navigation was handled, false when only the map was left
     */
    fun navigateBack(): Boolean {
        val current = backStack.lastOrNull()
        // Quitter le mur sans se connecter abandonne la destination qui l'avait provoqué : sinon elle
        // ressusciterait à une connexion ultérieure, lancée depuis un tout autre écran.
        if (current == Route.Auth) pendingRoute = null
        return when {
            current == null || (current == Route.Map && backStack.size == 1) -> {
                false
            }

            backStack.size > 1 -> {
                backStack.removeLastOrNull()
                true
            }

            else -> {
                backStack[0] = Route.Map
                true
            }
        }
    }

    /**
     * Marqueur tapé à côté de la colonne grand écran (spec §5) : si la colonne montre déjà une
     * fiche, celle-ci est remplacée plutôt qu'empilée, pour que la colonne reste en place et que
     * le retour ramène à l'écran d'où elle vient, pas à la fiche précédente. Sinon la fiche
     * s'empile sur la liste ouverte (favoris, récoltes, mes arbres).
     */
    fun openTreeInPanel(treeId: Long) {
        val target = Route.TreeDetail(treeId)
        val current = backStack.lastOrNull()
        when {
            current == target -> Unit
            current is Route.TreeDetail -> replaceWith(target)
            else -> navigate(target)
        }
    }

    /** Pendant de [openTreeInPanel] pour une plaque de lieu : une fiche de lieu ouverte est remplacée. */
    fun openSiteInPanel(siteId: Long) {
        val target = Route.SiteDetail(siteId)
        val current = backStack.lastOrNull()
        when {
            current == target -> Unit
            current is Route.SiteDetail -> replaceWith(target)
            else -> navigate(target)
        }
    }

    /**
     * Replace the current route with a new route.
     *
     * Useful for scenarios like:
     * - Post-login redirect (replace Auth with Map)
     * - Completing a flow and returning to the previous screen
     *
     * @param route The new route to display
     */
    fun replaceWith(route: Route) {
        if (route.requiresAuth && !isAuthenticated()) {
            // Same guard as navigate(): keep the current screen underneath so
            // backing out of Auth returns to it
            pendingRoute = route
            if (backStack.lastOrNull() != Route.Auth) {
                backStack.add(Route.Auth)
            }
            return
        }
        backStack.removeLastOrNull()
        backStack.add(route)
    }

    /**
     * Clear the entire back stack and navigate to a new route.
     *
     * Useful for:
     * - Logout (clear everything and go to Auth)
     * - Deep reset to home screen
     *
     * @param route The new root route
     */
    fun resetTo(route: Route) {
        // La destination en attente appartient a la session qu'on quitte : sans cet oubli, elle
        // ressusciterait a la connexion suivante — potentiellement celle d'un autre utilisateur
        // sur le meme appareil (#48).
        pendingRoute = null
        backStack.clear()
        backStack.add(route)
    }

    /**
     * Fin d'une création (arbre ou lieu) : retour à la carte seule, d'où qu'on soit parti. La
     * sélection et l'avis « ajouté à la carte » vivent dans l'écran carte, qu'un simple retour
     * laisserait sous Profil, Mes arbres ou la fiche d'arbre d'où l'assistant a été ouvert.
     */
    fun returnToMapAfterCreation() = resetTo(Route.Map)

    /**
     * Check if we can navigate back.
     *
     * @return true if there are multiple entries in the back stack
     */
    val canNavigateBack: Boolean
        get() = backStack.size > 1
}

/**
 * Remember NavigationState across recompositions and configuration changes.
 *
 * This composable creates a NavigationState that:
 * - Survives configuration changes (screen rotation, etc.)
 * - Survives process death (on Android)
 * - Uses polymorphic serialization for iOS/Web support
 *
 * @param startRoute The initial route to display
 * @param isAuthenticated Lambda that returns true if the user is authenticated
 * @return A stable NavigationState instance
 */
@Composable
fun rememberNavigationState(
    startRoute: Route = Route.Map,
    isAuthenticated: () -> Boolean = { false },
): NavigationState {
    // IMPORTANT: Use fruxNavConfig for iOS/Web support
    // Without this config, rememberNavBackStack() will crash on iOS/Web
    @Suppress("UNCHECKED_CAST")
    val backStack = rememberNavBackStack(fruxNavConfig, startRoute) as NavBackStack<Route>

    val pendingRouteState = rememberSaveable(saver = PendingRouteSaver) { mutableStateOf<Route?>(null) }

    return remember(backStack, isAuthenticated, pendingRouteState) {
        NavigationState(backStack, isAuthenticated, pendingRouteState)
    }
}

private val pendingRouteJson = Json { ignoreUnknownKeys = true }

/** Le sérialiseur scellé de [Route] suffit sur toutes les cibles, sans réflexion. */
internal fun encodePendingRoute(route: Route): String = pendingRouteJson.encodeToString(Route.serializer(), route)

/** Une sauvegarde illisible (version antérieure, état abîmé) est oubliée plutôt que de faire planter. */
internal fun decodePendingRoute(raw: String): Route? =
    runCatching { pendingRouteJson.decodeFromString(Route.serializer(), raw) }.getOrNull()

internal val PendingRouteSaver: Saver<MutableState<Route?>, String> =
    Saver(
        save = { state -> state.value?.let(::encodePendingRoute) },
        restore = { raw -> mutableStateOf(decodePendingRoute(raw)) },
    )
